import { CSSProperties, MutableRefObject, ReactNode, useEffect, useRef } from 'react'

import classNames from 'classnames'

import { markerZIndex } from '@web/components/Map/New/constant'
import { MapContext } from '@web/components/Map/New/context'
import { useFitBounds, useMap, useMapListener, useMarkers } from '@web/components/Map/New/hooks'
import { CustomMarkerOption } from '@web/components/Map/New/types'
import links from '@web/libs/constants/links'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import WithLazyLoadScript from '@web/pages/v5/components/WithLazyLoadScript'
import BackButtonArea from '@web/ui/components/ContentWithMap/BackButtonArea'

import styles from './index.module.scss'

export interface MapProps {
  /**
   * 외부 조작시 전달할 지도객체용 ref
   */
  mapRef?: MutableRefObject<naver.maps.Map | undefined>
  style?: CSSProperties
  className?: string
  /**
   * 마커 표시를 위한 option, id를 식별자로 삼습니다.
   */
  markerOptions?: CustomMarkerOption[]
  /**
   * 마커 표시 후 fitBound 를 수행할지 여부
   */
  enableFitBounds?: boolean
  /**
   * 선택된 마커 (id 를 사용합니다.)
   */
  selectedMarker?: CustomMarkerOption | null
  /**
   * 마커 선택시 콜백 (e.g. 선택 마커를 업데이트)
   */
  onMarkerSelect?: (marker: CustomMarkerOption | null, event: any) => void
  /**
   * 마커 선택시 지도 중심 이동할지 여부
   */
  panToMarkerSelect?: boolean
  /**
   * 페이지를 전체를 차지하는 full view 인지 여부
   */
  fullView?: boolean
  /**
   * MapContext 로 map 객체를 공유받을 children
   */
  children?: ReactNode
  /**
   * map 객체 생성시 전달할 옵션
   */
  initMapOptions?: naver.maps.MapOptions
  /**
   * 지도 클릭시 콜백
   */
  onClick?: () => void
  /**
   * 지도 클릭시 콜백
   */
  onMapReady?: (map: naver.maps.Map) => void
  /**
   * 지도 조작 시 클릭 코드
   */
  mapMoveClickCode?: string
}
/**
 * 참고: [Maps API v3 사내 Docs](https://pages.oss.navercorp.com/Maps/maps.js/docs/naver.maps.html)
 */
const MapContainer = ({
  className,
  enableFitBounds = false,
  markerOptions = [],
  onMarkerSelect,
  mapRef,
  selectedMarker,
  style,
  panToMarkerSelect = true,
  children,
  fullView = false,
  initMapOptions,
  onClick,
  onMapReady,
  mapMoveClickCode,
}: MapProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const { map } = useMap(containerRef, initMapOptions, onMapReady)
  if (mapRef) mapRef.current = map

  const markers = useMarkers(map, markerOptions)
  useFitBounds(map, markers, enableFitBounds)

  useMapListener(map, 'click', event => {
    // 오로지 마커가 아닌 빈맵 클릭시에만 호출하므로 선택된 마커 초기화 이벤트로 사용
    onMarkerSelect?.(null, event)
  })

  useMapListener(map, 'dragend', event => {
    mapMoveClickCode &&
      sendClickLog({
        event,
        area: mapMoveClickCode,
      })
  })

  // 클릭 이벤트 달아주기
  useEffect(() => {
    if (!map) return () => {}

    const listeners = markers.map(({ marker, option }) => {
      const isSelected = option.id === selectedMarker?.id

      marker.setZIndex(isSelected ? markerZIndex.SELECTED : option.zIndex ?? markerZIndex.NORMAL)

      if (option.selectedIcon && option.icon) {
        marker.setIcon(isSelected ? option.selectedIcon : option.icon)
      }

      const listener = naver.maps.Event.addListener(marker, 'click', event => {
        onMarkerSelect?.(option, event)
        option.clickCode &&
          sendClickLog({
            event,
            area: option.clickCode,
            contentId: option.id,
            placeId: option.placeId,
          })
        if (panToMarkerSelect) map.panTo(marker.getPosition())
      })

      return listener
    })

    return () => naver.maps.Event.removeListener(listeners)
  }, [markers, map, selectedMarker, onMarkerSelect, panToMarkerSelect])

  return (
    <MapContext.Provider value={map}>
      {fullView ? (
        <>
          <div className={styles.dynamic_map_area} onClick={onClick}>
            <div className={styles.dynamic_map}>
              <div ref={containerRef} style={style} className={classNames(styles.map_container, className)} />
            </div>
          </div>
          {children}
        </>
      ) : (
        <>
          <div ref={containerRef} style={style} className={className} onClick={onClick} />
          {children}
        </>
      )}
    </MapContext.Provider>
  )
}

const Map = (props: MapProps) => {
  return (
    <WithLazyLoadScript src={links.MAP_3}>
      <MapContainer {...props} />
    </WithLazyLoadScript>
  )
}

export const VectorMap = ({ initMapOptions, ...props }: MapProps) => {
  return (
    <WithLazyLoadScript src={links.MAP_3}>
      <WithLazyLoadScript src={links.MAP_3_GL_PLUGIN}>
        <MapContainer {...props} initMapOptions={{ ...initMapOptions, gl: true }} />
      </WithLazyLoadScript>
    </WithLazyLoadScript>
  )
}

Map.ButtonArea = BackButtonArea

export default Map
